// CompleteProfilePage — first login: collect name + M-Pesa phone number.
// Rendered inside AuthLayout, which supplies the navy brand header + card shell.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { phoneSchema } from "@dm-lms/shared/validations";
import type { User } from "@dm-lms/shared/types";
import { useAuth } from "../../hooks/useAuth.js";
import { postJson } from "../../hooks/useApi.js";

export default function CompleteProfilePage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState(user?.name ?? "");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    const phoneResult = phoneSchema.safeParse(phone);
    if (!phoneResult.success) {
      setPhoneError(phoneResult.error.issues[0]?.message ?? "Invalid phone number");
      return;
    }
    setPhoneError(null);

    setIsSubmitting(true);
    try {
      const res = await postJson<User>("/api/auth/complete-profile", { name, phone });
      if (!res.ok) {
        setError(res.error);
        setIsSubmitting(false);
        return;
      }
      navigate("/app", { replace: true });
    } catch {
      setError("Unable to save your profile. Please try again.");
      setIsSubmitting(false);
    }
  }

  return (
    <div>
      <h1 className="font-display text-xl font-semibold text-dm-navy text-center">Complete Your Profile</h1>
      <p className="mt-2 text-sm text-dm-slate text-center">
        We need a few more details to set up your account
      </p>

      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-dm-navy">
            Full Name
          </label>
          <input
            id="name"
            type="text"
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-dm-navy focus:border-dm-royal focus:outline-none focus:ring-1 focus:ring-dm-royal"
          />
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-dm-navy">
            M-Pesa Phone Number
          </label>
          <input
            id="phone"
            type="tel"
            required
            placeholder="+254 700 000 000"
            value={phone}
            onChange={(event) => {
              setPhone(event.target.value);
              if (phoneError) setPhoneError(null);
            }}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-dm-navy focus:border-dm-royal focus:outline-none focus:ring-1 focus:ring-dm-royal"
          />
          <p className="mt-1 text-xs text-dm-slate">Used for course payments via M-Pesa</p>
          {phoneError && <p className="mt-1 text-sm text-red-600">{phoneError}</p>}
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full rounded-md bg-dm-navy px-4 py-3 text-sm font-medium text-white transition hover:bg-dm-blue disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSubmitting ? "Saving…" : "Complete Setup"}
        </button>
      </form>
    </div>
  );
}
